import React, { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { toast } from '../../utils/toast'
import { MagnifyingGlassIcon, PlusIcon } from '@phosphor-icons/react'
import { GuildModal, GuildFormData } from '../../components/GuildModal/GuildModal'
import GuildsTable from '../../components/GuildsTable/GuildsTable'
import { Role } from '../../types/Roles'
import {
  useCreateGuildMutation,
  useLazyGetAllRolesQuery,
  useUpdateGuildMutation,
} from '../../redux/role.endpoints'
import Loader from '../../components/Loader/Loader'

const GuildView = () => {
  const [guilds, setGuilds] = useState<Role[]>([])
  const [searchValue, setSearchValue] = useState('')
  const [modal, setModal] = useState<null | { mode: 'add' | 'edit'; row?: Role }>(
    null,
  )

  const [getGuilds, { data, isLoading }] = useLazyGetAllRolesQuery()
  const [createGuild] = useCreateGuildMutation()
  const [updateGuild] = useUpdateGuildMutation()

  useEffect(() => {
    getGuilds()
  }, [getGuilds])

  useEffect(() => {
    setGuilds(data || [])
  }, [data])

  const visibleGuilds = useMemo(
    () =>
      guilds.filter(guild =>
        guild.role.toLowerCase().includes(searchValue.toLowerCase()),
      ),
    [guilds, searchValue],
  )

  const handleSaveGuild = async (values: GuildFormData) => {
    try {
      if (modal?.mode === 'edit' && modal.row) {
        await updateGuild({
          id: modal.row.id,
          role: values.name,
          type: values.type,
          salary: modal.row.salary,
        }).unwrap()
      } else {
        await createGuild({
          role: values.name,
          type: values.type,
          salary: 0,
        }).unwrap()
      }
      getGuilds()
      setModal(null)
    } catch (error: any) {
      const action = modal?.mode === 'edit' ? 'updating' : 'creating'
      toast.error(
        `An error occurred when ${action} the guild: ${
          error?.data?.error ?? 'Unknown error'
        }`,
        { toastId: 'guild-save-error' },
      )
      return false
    }
  }

  const openEdit = (guild: Role) => setModal({ mode: 'edit', row: guild })

  return (
    <div className="container my-4 manage-guilds">
      <Helmet title="Time Off - Guilds" />

      <div className="manage-guilds__header">
        <h1 className="manage-guilds__title">Manage Guilds</h1>
        <button
          type="button"
          className="manage-guilds__add-btn"
          onClick={() => setModal({ mode: 'add' })}
        >
          <PlusIcon size={16} weight="regular" color="#ffffff" />
          Add New Guild
        </button>
      </div>

      <div className="manage-guilds__filters">
        <div className="manage-guilds__search">
          <input
            type="text"
            className="manage-guilds__search-input"
            placeholder="Search by Guild"
            aria-label="Search by Guild"
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
          />
          <MagnifyingGlassIcon
            size={16}
            weight="regular"
            className="manage-guilds__search-icon"
          />
        </div>
      </div>

      {isLoading ? <Loader /> : (<GuildsTable
        guilds={visibleGuilds}
        onRowClick={openEdit}
        onEdit={openEdit}
      />
      )}

      <GuildModal
        show={modal !== null}
        mode={modal?.mode ?? 'add'}
        initialValues={
          modal?.row
            ? { name: modal.row.role, type: modal.row.type }
            : undefined
        }
        onClose={() => setModal(null)}
        onSubmit={handleSaveGuild}
      />
    </div>
  )
}

export default GuildView
